import { Modal, SystemText, Table } from '@salesperformanceint/e4shared';
import { skillColour } from 'helpers/metrics';
import { translateSkill } from 'helpers/translations';
import React from 'react';
import { useSelector } from 'react-redux';

export const PathwaySkills = ({ showLearn2, showQuickCheck, close, title }) => {
    const [examResults, setExamResults] = React.useState([]);
    const [examSortBy, setExamSortBy] = React.useState('title');
    const [examDescending, setExamDescending] = React.useState(false);

    const [quizResults, setQuizResults] = React.useState([]);
    const [quizSortBy, setQuizSortBy] = React.useState('skill');
    const [quizDescending, setQuizDescending] = React.useState(false);

    const [quickCheckSkills, setQuickCheckSkills] = React.useState(null);
    const [quickCheckResults, setQuickCheckResults] = React.useState([]);
    const [quickCheckSortBy, setQuickCheckSortBy] = React.useState('title');
    const [quickCheckDescending, setQuickCheckDescending] = React.useState(false);

    const [learn2Items, setLearn2Items] = React.useState([]);

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        if (Array.isArray(showLearn2)) {
            setLearn2Items(
                showLearn2.map(item => ({
                    ...item,
                    skills: JSON.parse(item.skills),
                }))
            );
        }
    }, [showLearn2]);

    React.useEffect(() => {
        if (Array.isArray(showQuickCheck)) {
            setQuickCheckSkills(showQuickCheck.flatMap(item => JSON.parse(item.skills)));
        }
    }, [showQuickCheck]);

    React.useEffect(() => {
        if (!learn2Items) {
            return;
        }

        setQuizResults([]);
        setExamResults([]);
        setQuickCheckResults([]);

        var quizResults = [];
        var examResults = [];

        learn2Items?.forEach(item => {
            if (item?.examResults) {
                examResults = [...examResults, ...item.examResults.map(s => translateSkill(user.language_Code, s))];
            }

            if (item?.skills) {
                quizResults = [
                    ...quizResults,
                    ...item.skills
                        .map(s => translateSkill(user.language_Code, s))
                        .map(s => ({
                            skill: s.title,
                            score: s.score,
                        })),
                ];
            }
        });

        const d = quizDescending ? -1 : 1;

        if (examResults) {
            var sortedExamResults = examResults.sort((a, b) => (a[examSortBy] > b[examSortBy] ? 1 * d : -1 * d));
            setExamResults(sortedExamResults);
        }

        if (quizResults) {
            var sortedQuizResults = quizResults.sort((a, b) => (a[quizSortBy] > b[quizSortBy] ? 1 * d : -1 * d));
            setQuizResults(sortedQuizResults);
        }
    }, [learn2Items]);

    React.useEffect(() => {
        if (!quickCheckSkills) {
            return;
        }

        setQuickCheckResults([]);

        const d = quickCheckDescending ? -1 : 1;

        const reuslts = quickCheckSkills
            .map(item => translateSkill(user.language_Code.toLowerCase(), item))
            .map(item => ({
                title: item.title,
                baselineScore: item.scores?.baseline,
                currentScore: item.scores?.current,
                finalScore: item.scores?.final || -1,
            }))
            .sort((a, b) => (a[quickCheckSortBy] > b[quickCheckSortBy] ? 1 * d : -1 * d));

        setQuickCheckResults(reuslts);
    }, [quickCheckSkills]);

    React.useEffect(() => {
        const d = examDescending ? -1 : 1;

        setExamResults(prev =>
            [...prev].sort((a, b) => {
                if (a[examSortBy] === b[examSortBy]) return 0;
                return a[examSortBy] > b[examSortBy] ? d : -d;
            })
        );
    }, [examSortBy, examDescending]);

    const examTriggerSort = React.useCallback(
        column => {
            column === examSortBy && setExamDescending(!examDescending);
            setExamSortBy(column);
        },
        [examSortBy, examDescending]
    );

    React.useEffect(() => {
        const d = quizDescending ? -1 : 1;

        setQuizResults(prev =>
            [...prev].sort((a, b) => {
                if (a[quizSortBy] === b[quizSortBy]) {
                    return 0;
                }
                return a[quizSortBy] > b[quizSortBy] ? d : -d;
            })
        );
    }, [quizSortBy, quizDescending]);

    const quizTriggerSort = React.useCallback(
        column => {
            column === quizSortBy && setQuizDescending(!quizDescending);
            setQuizSortBy(column);
        },
        [quizSortBy, quizDescending]
    );

    React.useEffect(() => {
        const d = quickCheckDescending ? -1 : 1;

        setQuickCheckResults(prev =>
            [...prev].sort((a, b) => {
                if (a[quickCheckSortBy] === b[quickCheckSortBy]) return 0;
                return a[quickCheckSortBy] > b[quickCheckSortBy] ? d : -d;
            })
        );
    }, [quickCheckSortBy, quickCheckDescending]);

    const quickCheckTriggerSort = React.useCallback(
        column => {
            column === quickCheckSortBy && setQuickCheckDescending(!quickCheckDescending);
            setQuickCheckSortBy(column);
        },
        [quickCheckSortBy, quickCheckDescending]
    );

    let examTableSettings = {
        columns: [
            {
                label: SystemText('Learning/Skills/Exam/Table/AssessmentResults'),
                className: 'prism-test-result',
                dataProperty: 'title',
            },
            {
                label: SystemText('Learning/Skills/Exam/Table/FirstAttempt'),
                dataProperty: 'firstScore',
                className: 'text-center prism-test-result',
                format: x => <label className={`label label-light-${skillColour(x)}`}>{parseInt(x, 10)}%</label>,
            },
            {
                label: SystemText('Learning/Skills/Exam/Table/LatestAttempt'),
                dataProperty: 'latestScore',
                className: 'text-center prism-test-result',
                format: x => <label className={`label label-light-${skillColour(x)}`}>{parseInt(x, 10)}%</label>,
            },
        ],
        options: {
            hidePaging: true,
            tableClass: 'table table-striped',
            sortBy: examSortBy,
            ascending: !examDescending,
        },
    };

    let quizTableSettings = {
        columns: [
            {
                label: SystemText('Learning/Skills/Quiz/Table/Results'),
                className: 'prism-test-result',
                dataProperty: 'skill',
            },
            {
                label: '',
                dataProperty: 'score',
                className: 'text-center prism-test-result',
                format: x => <label className={`label label-light-${skillColour(x)}`}>{parseInt(x, 10)}%</label>,
            },
        ],
        options: {
            hidePaging: true,
            tableClass: 'table table-striped',
            sortBy: quizSortBy,
            ascending: !quizDescending,
        },
    };

    let quickCheckTableSettings = {
        columns: [
            {
                label: SystemText('Learning/Skills/QuickCheck/Table/Skill'),
                className: 'prism-test-result',
                dataProperty: 'title',
            },
            {
                label: SystemText('Learning/Skills/QuickCheck/Table/Baseline'),
                dataProperty: 'baselineScore',
                className: 'text-center prism-test-result',
                format: x => <label className={`label label-light-${skillColour(x)}`}>{parseInt(x, 10)}%</label>,
            },
            {
                label: SystemText('Learning/Skills/QuickCheck/Table/Current'),
                dataProperty: 'currentScore',
                className: 'text-center prism-test-result',
                format: x => <label className={`label label-light-${skillColour(x)}`}>{parseInt(x, 10)}%</label>,
            },
            {
                label: SystemText('Learning/Skills/QuickCheck/Table/Final'),
                dataProperty: 'finalScore',
                className: 'text-center prism-test-result',
                format: x => (
                    <label className={`${x > -1 ? `label label-light-${skillColour(x)}` : ''}`}>
                        {x > -1 ? `${parseInt(x, 10)}%` : '-'}
                    </label>
                ),
            },
        ],
        options: {
            hidePaging: true,
            tableClass: 'table table-striped',
            sortBy: quickCheckSortBy,
            ascending: !quickCheckDescending,
        },
    };

    const examTable = () => {
        return examResults?.length ? (
            <Table
                data={examResults}
                columns={examTableSettings.columns}
                options={examTableSettings.options}
                sort={examTriggerSort}
            />
        ) : null;
    };

    const quizTable = () => {
        return quizResults?.length ? (
            <Table
                data={quizResults}
                columns={quizTableSettings.columns}
                options={quizTableSettings.options}
                sort={quizTriggerSort}
            />
        ) : null;
    };

    const quickCheckTable = () => {
        return quickCheckResults?.length ? (
            <Table
                data={quickCheckResults}
                columns={quickCheckTableSettings.columns}
                options={quickCheckTableSettings.options}
                sort={quickCheckTriggerSort}
            />
        ) : null;
    };

    const showModal = () => {
        if (showLearn2 || showQuickCheck) {
            return true;
        }

        return false;
    };

    return (
        <Modal show={showModal()} title={title} close={close} footer={null}>
            {examTable()}
            {quizTable()}
            {quickCheckTable()}
        </Modal>
    );
};

export default PathwaySkills;
